import type { PortalSiteMobileAppDto } from '@supply-mover-2-0/shared';
import { cn, useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import type { StorePlatform } from '../lib/mobileApp';

/**
 * PD-16558 — the official App Store and Google Play badges, the same
 * unmodified artwork the welcome email uses (login's STORE_BADGES). Apple
 * and Google require their own badge art rather than a styled button, and
 * both at the same height.
 */
const BADGES = {
	appStore: {
		src: 'https://cdn.titanworkflow.com/titanworkflow/assets/img/store-badges/app-store-badge.png',
		alt: 'Download on the App Store',
		width: 120,
	},
	googlePlay: {
		src: 'https://cdn.titanworkflow.com/titanworkflow/assets/img/store-badges/google-play-badge.png',
		alt: 'Get it on Google Play',
		width: 134,
	},
} as const;

interface StoreBadgesProps {
	app: PortalSiteMobileAppDto;
	/** This device's store is listed first. */
	platform: StorePlatform;
	className?: string;
}

export function StoreBadges({
	app,
	platform,
	className,
}: StoreBadgesProps): JSX.Element | null {
	const { t } = useTranslation();
	const entries = [
		{ key: 'appStore', href: app.appStoreUrl, badge: BADGES.appStore },
		{ key: 'googlePlay', href: app.googlePlayUrl, badge: BADGES.googlePlay },
	];
	if (platform === 'android') {
		entries.reverse();
	}
	const shown = entries.filter(
		(entry): entry is (typeof entries)[number] & { href: string } =>
			entry.href != null,
	);
	if (shown.length === 0) {
		return null;
	}

	return (
		<div className={cn('flex flex-wrap items-center gap-3', className)}>
			{shown.map((entry) => (
				<a
					key={entry.key}
					href={entry.href}
					target="_blank"
					rel="noopener noreferrer"
					className="inline-block rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-portal-accent"
				>
					{/* 40px tall: Apple's minimum on-screen badge size. */}
					<img
						src={entry.badge.src}
						alt={t(entry.badge.alt)}
						width={entry.badge.width}
						height={40}
						className="block h-10 w-auto"
					/>
				</a>
			))}
		</div>
	);
}
